import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  ButtonGroup,
  Box,
  Divider,
  Modal,
} from '@shopify/polaris';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import {
  useWooBillingStatus,
  useWooBillingPlans,
  useWooBillingCheckout,
  useWooBillingPortal,
  useWooChangePlan,
  useWooCancelSubscription,
  useWooResumeSubscription,
  type WooPlanOffer,
} from '../hooks/api';

// WooCommerce billing (Stripe).
//
// - No subscription yet: pick a plan -> EMBEDDED Stripe Checkout, mounted here.
//   Embedded rather than hosted because the Stripe account is shared with other
//   products and the hosted page showed another brand's name mid purchase.
// - Already subscribed: the plan changes ON THE EXISTING subscription. Starting
//   a new checkout would open a second subscription and bill both, which is
//   exactly what "Choose" used to do from any paid plan.
// - Free is a plan on this page, not a button hidden in a portal: switching to
//   it ends the subscription at the end of the paid period, and can be undone.
const PLAN_COPY: Record<WooPlanOffer['plan'], { name: string; blurb: string }> = {
  STARTER: { name: 'Starter', blurb: 'Google Ads, Meta CAPI, GA4' },
  GROWTH: { name: 'Growth', blurb: '+ TikTok, Pinterest, Snapchat, advanced analytics' },
  PRO: { name: 'Pro', blurb: '+ alerts, CSV export, outbound webhooks, API access' },
};

type Interval = 'MONTHLY' | 'ANNUAL';
type Pending =
  | { kind: 'change'; plan: WooPlanOffer['plan']; interval: Interval }
  | { kind: 'free' };

function money(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);
}

function longDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

export function BillingWoo() {
  const { data: status, isLoading } = useWooBillingStatus();
  const { data: offers } = useWooBillingPlans();
  const checkout = useWooBillingCheckout();
  const portal = useWooBillingPortal();
  const changePlan = useWooChangePlan();
  const cancel = useWooCancelSubscription();
  const resume = useWooResumeSubscription();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [session, setSession] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [interval, setCadence] = useState<Interval>('MONTHLY');
  const [confirm, setConfirm] = useState<Pending | null>(null);

  const currentPlan = status?.plan || 'FREE';
  const subscribed = !!status?.hasSubscription;
  const annualOffered = !!status?.annualAvailable && (offers?.plans ?? []).some((p) => p.annual);

  // Open on the cadence the merchant actually pays, so the current plan is
  // marked current instead of looking like something to buy again.
  useEffect(() => {
    if (status?.interval) setCadence(status.interval);
  }, [status?.interval]);

  // loadStripe must not re-run on every render: a new promise remounts the
  // iframe and loses the form the merchant is filling in.
  const stripePromise = useMemo(
    () => (session ? loadStripe(session.publishableKey) : null),
    [session?.publishableKey],
  );

  const startCheckout = useCallback(
    async (plan: string) => {
      setError(null);
      setPendingPlan(plan);
      try {
        setSession(await checkout.mutateAsync({ plan, interval }));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start checkout.');
      } finally {
        setPendingPlan(null);
      }
    },
    [checkout, interval],
  );

  const runConfirmed = useCallback(async () => {
    if (!confirm) return;
    setError(null);
    try {
      if (confirm.kind === 'change') {
        await changePlan.mutateAsync({ plan: confirm.plan, interval: confirm.interval });
        // Name the plan: "done" says nothing to someone who just moved down.
        setNotice(`Done. You are now on the ${PLAN_COPY[confirm.plan].name} plan, billed ${confirm.interval === 'ANNUAL' ? 'yearly' : 'monthly'}.`);
      } else {
        const next = await cancel.mutateAsync();
        setNotice(`Done. Your ${currentPlan} plan stays active until ${longDate(next.currentPeriodEnd)}, then you move to Free.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change could not be made. Nothing was charged.');
    } finally {
      setConfirm(null);
    }
  }, [confirm, changePlan, cancel, currentPlan]);

  const keepPlan = useCallback(async () => {
    setError(null);
    try {
      await resume.mutateAsync();
      setNotice(`Done. Your ${currentPlan} plan continues.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not keep your plan.');
    }
  }, [resume, currentPlan]);

  const manage = useCallback(async () => {
    setError(null);
    try {
      const { url } = await portal.mutateAsync();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal.');
    }
  }, [portal]);

  const priceFor = (offer: WooPlanOffer) => (interval === 'ANNUAL' ? offer.annual : offer.monthly);

  return (
    <>
      <Page title="Billing">
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}
              {notice && (
                <Banner tone="success" onDismiss={() => setNotice(null)}>
                  {notice}
                </Banner>
              )}

              {session && stripePromise ? (
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        Payment
                      </Text>
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          setSession(null);
                          setError(null);
                        }}
                      >
                        Back to plans
                      </Button>
                    </InlineStack>
                    {/* Stripe redirects to the return_url once the payment
                        completes, so there is no success state to render here. */}
                    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: session.clientSecret }}>
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </BlockStack>
                </Card>
              ) : (
                <>
                  <Card>
                    <BlockStack gap="300">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h2" variant="headingMd">
                          Current plan
                        </Text>
                        <Badge tone="success">{isLoading ? '…' : currentPlan}</Badge>
                      </InlineStack>
                      {subscribed && status?.interval && (
                        <Text as="p" tone="subdued">
                          Billed {status.interval === 'ANNUAL' ? 'yearly' : 'monthly'}
                          {status.currentPeriodEnd && !status.cancelAtPeriodEnd
                            ? `, next renewal on ${longDate(status.currentPeriodEnd)}.`
                            : '.'}
                        </Text>
                      )}
                      {status?.cancelAtPeriodEnd && (
                        <Banner tone="warning">
                          <BlockStack gap="200">
                            <Text as="p">
                              Your {currentPlan} plan ends on {longDate(status.currentPeriodEnd)}. After that you move to
                              Free ({offers?.freeOrdersPerMonth ?? 15} tracked orders a month).
                            </Text>
                            <Box>
                              <Button onClick={keepPlan} loading={resume.isPending}>
                                Keep my plan
                              </Button>
                            </Box>
                          </BlockStack>
                        </Banner>
                      )}
                      {subscribed && (
                        <Box>
                          <Button variant="plain" onClick={manage} loading={portal.isPending}>
                            Invoices and payment method
                          </Button>
                        </Box>
                      )}
                    </BlockStack>
                  </Card>

                  <Card>
                    <BlockStack gap="400">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h2" variant="headingMd">
                          Plans
                        </Text>
                        {annualOffered && (
                          <ButtonGroup variant="segmented">
                            <Button pressed={interval === 'MONTHLY'} onClick={() => setCadence('MONTHLY')}>
                              Monthly
                            </Button>
                            <Button pressed={interval === 'ANNUAL'} onClick={() => setCadence('ANNUAL')}>
                              {`Yearly (save ${status?.annualDiscountPercent ?? 25}%)`}
                            </Button>
                          </ButtonGroup>
                        )}
                      </InlineStack>
                      <Text as="p" tone="subdued" variant="bodySm">
                        Prices in USD. Only tracked orders count toward the allowance: page views, product views,
                        add-to-carts and checkouts are unlimited.
                      </Text>

                      {/* Free is a plan like the others, so leaving a paid plan is one visible click. */}
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            Free
                          </Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {`$0 · up to ${offers?.freeOrdersPerMonth ?? 15} tracked orders a month`}
                          </Text>
                        </BlockStack>
                        {currentPlan === 'FREE' ? (
                          <Button disabled>Current</Button>
                        ) : status?.cancelAtPeriodEnd ? (
                          <Button disabled>{`From ${longDate(status.currentPeriodEnd)}`}</Button>
                        ) : subscribed ? (
                          <Button onClick={() => setConfirm({ kind: 'free' })}>Switch to Free</Button>
                        ) : null}
                      </InlineStack>

                      {(offers?.plans ?? []).map((offer) => {
                        const price = priceFor(offer);
                        const isCurrent =
                          offer.plan === currentPlan && (!subscribed || !status?.interval || status.interval === interval);
                        const samePlanOtherCadence = subscribed && offer.plan === currentPlan && !isCurrent;
                        return (
                          <BlockStack key={offer.plan} gap="300">
                            <Divider />
                            <InlineStack align="space-between" blockAlign="center">
                              <BlockStack gap="100">
                                <Text as="span" variant="bodyMd" fontWeight="semibold">
                                  {PLAN_COPY[offer.plan].name}
                                </Text>
                                <Text as="span" variant="bodyMd">
                                  {price
                                    ? interval === 'ANNUAL'
                                      ? `${money(price.amount, price.currency)} / year (${money(Math.round(price.amount / 12), price.currency)} / month)`
                                      : `${money(price.amount, price.currency)} / month`
                                    : 'Not available on this cadence'}
                                </Text>
                                <Text as="span" tone="subdued" variant="bodySm">
                                  {`Up to ${offer.ordersPerMonth.toLocaleString('en-US')} tracked orders a month · ${PLAN_COPY[offer.plan].blurb}`}
                                </Text>
                              </BlockStack>
                              {isCurrent ? (
                                <Button disabled>Current</Button>
                              ) : (
                                <Button
                                  variant="primary"
                                  disabled={!price}
                                  loading={checkout.isPending && pendingPlan === offer.plan}
                                  onClick={() =>
                                    subscribed
                                      ? setConfirm({ kind: 'change', plan: offer.plan, interval })
                                      : startCheckout(offer.plan)
                                  }
                                >
                                  {samePlanOtherCadence
                                    ? `Switch to ${interval === 'ANNUAL' ? 'yearly' : 'monthly'} billing`
                                    : subscribed
                                      ? 'Switch to this plan'
                                      : 'Choose'}
                                </Button>
                              )}
                            </InlineStack>
                          </BlockStack>
                        );
                      })}
                    </BlockStack>
                  </Card>
                </>
              )}
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>

      {/* Outside <Page>, so the overlay is never trapped by its stacking context. */}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'free' ? 'Switch to Free?' : 'Change your plan?'}
        primaryAction={{
          content: 'Confirm',
          onAction: runConfirmed,
          loading: changePlan.isPending || cancel.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConfirm(null) }]}
      >
        <Modal.Section>
          <Text as="p">
            {confirm?.kind === 'free'
              ? `Your ${currentPlan} plan stays active until ${longDate(status?.currentPeriodEnd ?? null)}, and nothing more is charged. Then you move to Free. You can change your mind until that date.`
              : confirm
                ? `You move to ${PLAN_COPY[confirm.plan].name}, billed ${confirm.interval === 'ANNUAL' ? 'yearly' : 'monthly'}, right away. Stripe credits the unused part of your current plan and charges the difference on your next invoice.`
                : ''}
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );
}
